'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
  image: string;
  imageAlt: string;
  category: string;
  complexity: string;
  roiTimeline: string;
  businessFunction: string;
  benefits: string[];
  implementationDuration: string;
  roiMetric: string;
  detailLink: string;
  features: string[];
  pricing: string;
}

interface ComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedServices: Service[];
  onRemoveService: (id: string) => void;
}

const ComparisonModal = ({ isOpen, onClose, selectedServices, onRemoveService }: ComparisonModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/95 backdrop-blur-sm">
      <div className="glassmorphic rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="ScaleIcon" size={28} className="text-primary" />
            <h2 className="text-2xl font-display font-bold text-foreground">
              Service Vergelijking
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-300"
          >
            <Icon name="XMarkIcon" size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {selectedServices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Icon name="InformationCircleIcon" size={48} className="text-muted-foreground mb-4" />
              <p className="text-lg text-muted-foreground">
                Selecteer services om te vergelijken
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {selectedServices.map((service) => (
                <div key={service.id} className="glassmorphic rounded-xl p-6 relative">
                  <button
                    onClick={() => onRemoveService(service.id)}
                    className="absolute top-4 right-4 p-1.5 rounded-lg bg-error/10 text-error hover:bg-error hover:text-error-foreground transition-all duration-300"
                  >
                    <Icon name="XMarkIcon" size={16} />
                  </button>

                  <div className="w-full h-32 rounded-lg overflow-hidden mb-4">
                    <AppImage
                      src={service.image}
                      alt={service.imageAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name={service.icon as any} size={20} className="text-primary" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-foreground">
                      {service.title}
                    </h3>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Categorie</span>
                      <span className="text-foreground font-semibold">{service.category}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Complexiteit</span>
                      <span className="text-foreground font-semibold">{service.complexity}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">ROI Timeline</span>
                      <span className="text-foreground font-semibold">{service.roiTimeline}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Implementatie</span>
                      <span className="text-foreground font-semibold">{service.implementationDuration}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Prijsindicatie</span>
                      <span className="text-foreground font-semibold">{service.pricing}</span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs font-semibold text-muted-foreground mb-2">Kenmerken:</p>
                    <ul className="space-y-1">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start space-x-2 text-xs text-foreground">
                          <Icon name="CheckIcon" size={12} className="text-primary mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    href={service.detailLink}
                    className="flex items-center justify-center space-x-2 w-full px-4 py-2.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 text-sm font-semibold"
                  >
                    <span>Bekijk details</span>
                    <Icon name="ArrowRightIcon" size={16} />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComparisonModal;